import React, { createContext, useContext } from "react";
import {
  useQuery,
  getUserBookmarks,
  toggleBookmark,
} from "wasp/client/operations";
import { Article } from "wasp/entities";
import { useAuth } from "wasp/client/auth";

interface BookmarksContextType {
  bookmarkedArticleIds: number[];
  toggleBookmark: (article: Article) => Promise<void>;
  isBookmarked: (articleId: number) => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const BookmarksContext = createContext<BookmarksContextType | undefined>(
  undefined,
);

export function BookmarksProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading: authLoading } = useAuth();
  const { data: bookmarks, isLoading: bookmarksLoading } = useQuery(
    getUserBookmarks,
    undefined,
    { enabled: !!user },
  );

  const handleToggleBookmark = async (article: Article) => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    await toggleBookmark({ articleId: article.id });
  };

  const bookmarkedArticleIds = (bookmarks ?? [])
    .map((bookmark) => bookmark.articleId)
    .filter((articleId): articleId is number => typeof articleId === "number");

  const isBookmarked = (articleId: number) => {
    if (!user) return false;
    return bookmarkedArticleIds.includes(articleId);
  };

  return (
    <BookmarksContext.Provider
      value={{
        bookmarkedArticleIds,
        toggleBookmark: handleToggleBookmark,
        isBookmarked,
        isLoading: authLoading || bookmarksLoading,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </BookmarksContext.Provider>
  );
}

export function useBookmarks() {
  const context = useContext(BookmarksContext);
  if (!context) {
    throw new Error("useBookmarks must be used within a BookmarksProvider");
  }
  return context;
}
